import type { PointerEvent, ReactNode } from 'react';

/**
 * Pieces of the public home page that move.
 *
 * Entrances are CSS (they run on the prerendered page before JavaScript
 * arrives); pointer effects attach after hydration and only write CSS custom
 * properties, so the server's markup and the first client render never differ.
 * Reduced motion is honoured in site.css.
 */

/**
 * The section numbering, elevated into a real editorial device: a registry
 * numeral, a hairline rule, and the running total — the way a surveyed
 * document paginates itself, not a decorative label bolted onto a heading.
 */
export function SectionMark({ index, total }: { index: number; total: number }) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <p className="section-mark numeric" aria-hidden="true">
      <span className="section-mark-n">{pad(index)}</span>
      <span className="section-mark-rule" />
      <span className="section-mark-total">{pad(total)}</span>
    </p>
  );
}

/**
 * A card with a light that follows the pointer. The handler only sets two
 * custom properties; the effect itself is CSS.
 */
export function Spotlight({ children, className }: { children: ReactNode; className?: string }) {
  function onMove(event: PointerEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
  }
  return (
    <div className={['spot', className || ''].filter(Boolean).join(' ')} onPointerMove={onMove}>
      {children}
    </div>
  );
}

/**
 * The void mark: how this register notes a failure.
 *
 * Not a red X — a ledger does not paint a bad entry in alarm colours, it
 * strikes the line. A single diagonal stroke through a ruled tag, in ink,
 * reads as "voided" rather than "error", and stays inside the brand palette
 * (teal / blue / grey) where the semantic danger token never was invited.
 */
export function VoidMark() {
  return (
    <svg className="void-mark" viewBox="0 0 28 28" role="presentation" aria-hidden="true" focusable="false">
      <rect x="1.25" y="1.25" width="25.5" height="25.5" rx="3" />
      <line x1="5.5" y1="22.5" x2="22.5" y2="5.5" />
    </svg>
  );
}
